<template>
  <div v-if="activity">
    <b-row v-if="!activity.isNew">
      <b-col md="12">
        <router-link :to="{ name: 'controlactivity', params: { id: activity.id } }">
          {{ $tc("activity") }}
        </router-link>
      </b-col>
    </b-row>
    <b-row v-if="!activity.isNew && (activity.info || activity.answer)">
      <b-col md="12" class="my-2">
        {{ $tc("document") }}
        <router-link v-if="activity.info" :to="{ name: 'inforouter', params: { id: activity.info.id } }">
          {{ activity.info.name }}
        </router-link>
        <router-link
          v-if="activity.answer"
          :to="{ name: 'protocolrouter', params: { id: activity.answer.protocol.id } }"
        >
          {{ activity.answer.protocol.questionnaire.name }}
        </router-link>
      </b-col>
    </b-row>
    <b-row class="my-1" v-if="!activity.isNew && (activity.status < 3 || !disableFields)">
      <b-col md="6">
        <b-input-group>
          <pw-button cy-id="approveSign" className="outline-success" @accepted="approveProtocol(true)">
            <i class="fa fa-thumbs-up mr-2"></i>
            {{ $t("approved") }}
          </pw-button>
        </b-input-group>
        <b-input-group>
          <pw-button cy-id="rejcectSign" className="outline-danger" @accepted="approveProtocol(false)">
            <i class="fa fa-thumbs-down mr-2"></i>
            {{ $t("notApproved") }}
          </pw-button>
        </b-input-group>
      </b-col>
    </b-row>
    <b-row class="my-1" v-if="disableFields && activity.status >= 3">
      <b-col md="6">
        <b-button className="outline-success" v-if="activity.status == 4" disabled>
          <i class="fa fa-thumbs-up mr-2"></i>
          {{ $t("approved") }}
        </b-button>
        <b-button className="outline-danger" v-if="activity.status == 3" disabled>
          <i class="fa fa-thumbs-down mr-2"></i>
          {{ $t("notApproved") }}
        </b-button>
      </b-col>
    </b-row>
    <b-row class="my-2">
      <b-col md="12" v-if="activityDone">
        <VueSignaturePad
          height="100px"
          width="320px"
          :options="signOptions"
          ref="signaturePad"
          style="border: 1px solid #000"
          class="mb-2"
        />
      </b-col>
      <b-col md="12" v-if="!disableFields && activityDone">
        <check-button
          v-if="!isSigned && !signActive"
          icon="fa-pen"
          variant="default"
          size="sm"
          class="mr-2"
          @accepted="activateSign"
        >
          {{ $tc("readReceiptSign") }}
        </check-button>
        <check-button
          v-if="signActive"
          icon="fa-check"
          className="outline-success"
          size="sm"
          class="mr-2"
          :require-confirm="true"
          @accepted="signActivity()"
        >
          {{ $tc("save") }}
        </check-button>
        <check-button
          v-if="isSigned || signActive"
          icon="fa-times"
          className="outline-warning"
          size="sm"
          class="mr-2"
          :require-confirm="true"
          @accepted="clearSignature"
        >
          {{ $tc("clear") }}
        </check-button>
      </b-col>
    </b-row>
    <b-row class="my-1">
      <b-col md="6">
        <responsible-selector :editedActivity="activity" :disabled="disableFields || activity.status > 1 || isSigned" />
      </b-col>
    </b-row>
    <b-row class="my-2" v-if="!activity.isNew">
      <b-col md="12">
        <strong class="mb-n1">{{ $t("comment") }}</strong>
        <text-editor :disabled="disableFields" v-model="activity.description" :minHeight="50"></text-editor>
      </b-col>
    </b-row>
    <b-row class="my-2">
      <b-col md="6">
        <p class="mb-n1">{{ $t("deadline") }}</p>
        <b-input-group>
          <b-form-input
            v-model.lazy="$v.activity.deadline_date.$model"
            v-model="activity.deadline_date"
            type="date"
            :disabled="disableFields"
          />
          <error-list :field="$v.activity.responsible"></error-list>
        </b-input-group>
      </b-col>
      <b-col md="6" v-if="!activity.isNew">
        <p class="mb-n1">{{ $t("status") }}</p>
        <b-form-select v-model="activity.status" :options="activitySignTypes" :disabled="true"></b-form-select>
        <span v-if="activity.signature">{{ activity.signature }}</span>
      </b-col>
    </b-row>
    <b-row v-if="!activity.isNew">
      <b-col v-if="$userCan('change_activity')" md="12" class="mt-4">
        <b-button variant="outline-dark" @click="activateEdit()">
          <span class="icon">
            <i class="fa fa-edit"></i>
            {{ $t("modify") }}
          </span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CompanyQuestionnaireService from "@/services/CompanyQuestionnaireService";
import Activity from "@/models/Activity";
import ActivityCategoryService from "@/services/ActivityCategoryService";
import TextEditor from "@/components/TextEditor/TextEditor";
import moment from "moment";
import { ACTIVITY_STATUS_TYPES } from "@/constants";
import ResponsibleSelector from "@/components/Activities/ActivityResponsibleSelector";

export default {
  name: "SignModalForm",
  components: { TextEditor, ResponsibleSelector },
  props: {
    editedActivity: {
      type: Activity,
      required: false,
      default: null
    },
    isInfoType: {
      type: Boolean,
      required: false,
      default: true
    },
    objectName: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      activity: this.editedActivity,
      activityCategories: [],
      companyQuestionnaires: [],
      editFields: false,
      allowEdit: false,
      loading: false,
      id: this.$route.params.id,
      signOptions: {
        minWidth: 1,
        maxWidth: 1.5
      },
      signActive: false
    };
  },
  validations: {
    activity: Activity.validations
  },
  mounted() {
    this.getQuestionnaires();
    this.getActivityCategories();

    if (this.activity.isNew) {
      this.activity.name = this.$t("review") + " " + this.objectName;
      this.activity.activity_type = "1";
    }

    if (
      this.activity.isNew ||
      (this.$userCan("change_activity") &&
        (this.$userIsAdmin() || this.activity.responsible == null || this.activity.responsible.id == this.$user.person))
    ) {
      this.allowEdit = true;
    }
    this.signPadConfig();
  },
  watch: {
    editedActivity(activity) {
      this.activity = activity;
      if (this.activity) {
        this.signPadConfig();
      }
    },
    signActive() {
      //Dont get emit with parameter to work. So use 2 different functions
      if (this.signActive) {
        this.$emit("signOngoing");
      } else {
        this.$emit("signDisabled");
      }
    }
  },
  computed: {
    ...mapGetters("constants", ["activitySignTypes"]),
    disableFields() {
      return !(this.activity.isNew || this.editFields) || !this.allowEdit;
    },
    activityDone() {
      return this.activity && this.activity.status == ACTIVITY_STATUS_TYPES.DONE;
    },
    isSigned() {
      return this.activity.field6 != null && this.activity.field6 != "";
    }
  },
  methods: {
    getActivityCategories() {
      ActivityCategoryService.getActivityCategoriesByCompanyIncludingSystem(this.$userSelectedCompanyId()).then(
        (activityCategories) => {
          this.activityCategories = activityCategories;
        }
      );
    },
    getQuestionnaires() {
      CompanyQuestionnaireService.getCompanyQuestionnairesByCompanyAndIsActive(this.$userSelectedCompanyId()).then(
        (companyQuestionnaires) => {
          this.companyQuestionnaires = companyQuestionnaires;
        }
      );
    },
    approveProtocol(approved) {
      const today = moment().format("YYYY-MM-DD HH:MM:SS");
      this.activity.signature = this.$user.first_name + " " + this.$user.last_name + " - " + today;

      if (approved) {
        this.activity.status = 4;
      } else {
        this.clearSignature();
        this.activity.status = 3;
      }

      this.signPadConfig();
      this.$emit("signed");
    },
    updateDepartment(person) {
      if (person && person.departments.length > 0) {
        this.activity.field3 = person.departments[0].name;
      }
    },
    activateEdit(activate) {
      if (activate) {
        this.editFields = true;
      } else {
        this.editFields = !this.editFields;
      }

      if (this.editFields) {
        this.signPadConfig();
      } else {
        this.signActive = false;
      }
    },
    activateSign() {
      this.signActive = true;

      if (!this.isSigned && this.$refs.signaturePad) {
        this.$refs.signaturePad.openSignaturePad();
      }
    },
    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }

      if (this.isSigned) {
        this.activity.field6 = "";
        this.signActive = false;
        this.$emit("signed");

        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.lockSignaturePad();
        }
      }
    },
    signActivity() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      this.activity.field6 = data;
      this.signActive = false;
      this.$refs.signaturePad.lockSignaturePad();
      this.$emit("signed");
    },
    signPadConfig() {
      if (this.activity && this.activity.field6) {
        if (this.$refs.signaturePad && this.$refs.signaturePad.signaturePad.canvas.height > 0) {
          this.$refs.signaturePad.fromDataURL(this.activity.field6);
          this.$refs.signaturePad.lockSignaturePad();
        } else {
          this.$nextTick(() => {
            this.$refs.signaturePad.resizeCanvas();
            this.signPadConfig();
          });
        }
      } else if (this.$refs.signaturePad) {
        this.$refs.signaturePad.lockSignaturePad();
        this.$nextTick(() => {
          if (this.$refs.signaturePad) {
            this.$refs.signaturePad.resizeCanvas();
          }
        });
      }
    }
  }
};
</script>

<style scoped lang="scss"></style>
